import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  Card,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import ReactDOM from "react-dom";
import { PermissionsContext } from "../../utils/PermissionsContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import api from "../../utils/api";

import MemberCard from "./Details/MemberCard";
import StatusChip from "./Details/StatusChip";
import RequestDetailsPanel from "./Details/RequestDetailsPanel";
import Calendar from "../Calendar";
import ArchitecturalRequestComments from "./ArchitecturalRequestComments";
import ArchitecturalRequestVisits from "./ArchitecturalRequestVisits";
import ArchitecturalRequestPrintContent from "./ArchitecturalRequestPrintContent";
import ArchitecturalRequestLetterView from "./ArchitecturalRequestLetterView";
import FeesTab from "./FeesTab";
import ARConditionsPanel from "./ARConditionsPanel";
import ARStatusHistoryTab from "./ARStatusHistoryTab";
import ARCompletionPhotosTab from "./ARCompletionPhotosTab";
import ARInspectorClockTab from "./ARInspectorClockTab";

const ArchitecturalRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = sessionStorage.getItem("user");
  let currentUserId = null;

  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      currentUserId = parsedUser?.userId ?? null;
    } catch (e) {
      console.error("Failed to parse user from sessionStorage", e);
    }
  }

  const [requestDetails, setRequestDetails] = useState(null);
  const detailsPanelRef = useRef();

  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [comments, setComments] = useState([]);
  const [contractor, setContractor] = useState(null);
  const [colors, setColors] = useState([]);
  const [letterLogs, setLetterLogs] = useState([]);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const printRef = useRef();
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);

        const requestResp = await api.get(`/architectural-requests/${id}`);
        const requestData = requestResp.data;

        let memberData = null;
        if (requestData.property_id) {
          const memberResp = await api.get(`/members/${requestData.property_id}`);
          memberData = memberResp.data;
        }

        const priorityResp = await api.get(`/priority-levels`);
        const priorities = priorityResp.data || [];

        const matchedPriority = priorities.find(
          (p) => p.priority_value === requestData.priority
        );

        requestData.priority_color = matchedPriority?.color_code || "#2f63ad";

        setRequestDetails(requestData);
        setMember(memberData);
      } catch (error) {
        console.error("Failed to fetch details:", error);
        setError("Failed to fetch details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const textFieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const handleTabChange = (_, newVal) => setActiveTab(newVal);
  const priorityColor = requestDetails?.priority_color || "#2f63ad";

  const tabs = useMemo(() => {
    const builtTabs = [];

    if (hasPermission("view_architectural_request_details")) {
      builtTabs.push({ key: "details", label: "Details" });
    }

    if (hasPermission("view_architectural_request_letters")) {
      builtTabs.push({ key: "letters", label: "Arch Letters" });
    }

    if (hasPermission("view_architectural_request_calendar")) {
      builtTabs.push({ key: "calendar", label: "Calendar" });
    }

    if (hasPermission("view_architectural_request_comments")) {
      builtTabs.push({ key: "comments", label: "Comments" });
    }

    if (hasPermission("view_architectural_request_visits")) {
      builtTabs.push({ key: "visits", label: "Visits" });
    }

    if (hasPermission("view_architectural_request_fees")) {
      builtTabs.push({ key: "fees", label: "Fees" });
    }

    // Conditions tab - always visible for admins
    builtTabs.push({ key: "conditions", label: "Conditions" });
    builtTabs.push({ key: "history",    label: "History" });
    builtTabs.push({ key: "photos",     label: "Completion Photos" });
    builtTabs.push({ key: "inspector",  label: "Inspector Clock" });

    return builtTabs;
  }, [permissions]);

  useEffect(() => {
    if (activeTab > Math.max(tabs.length - 1, 0)) {
      setActiveTab(0);
    }
  }, [tabs, activeTab]);

  const activeKey = tabs[activeTab]?.key;

  const handleBack = () => {
    const from = location.state?.from;

    if (from) {
      navigate(from, { state: { reload: true } });
      return;
    }

    if (window.history.length > 2) {
      navigate(-1);
      return;
    }

    navigate("/home/architecturalrequest", { state: { reload: true } });
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=960,height=900");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Architectural Request #${requestDetails?.id || ""}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { background: #fff; font-family: 'Segoe UI', Arial, sans-serif; color: #1a202c; }
    img { max-width: 100%; display: block; }
    table { border-collapse: collapse; }
    @media print {
      html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      .no-print { display: none !important; }
    }
    .print-btn-bar { position: fixed; top: 0; right: 0; z-index: 9999; padding: 10px 16px; background: rgba(255,255,255,0.95); border-bottom-left-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); display: flex; gap: 8px; }
    .print-btn { background: #2f63ad; color: #fff; border: none; padding: 8px 20px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
    .print-btn:hover { background: #1e3a5f; }
    .close-btn { background: #6b7280; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 14px; cursor: pointer; font-family: inherit; }
  </style>
</head>
<body>
  <div class="print-btn-bar no-print">
    <button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>
  <div style="padding-top:56px">${printContents}</div>
  <script>
    var imgs = document.images, loaded = 0, total = imgs.length;
    function tryPrint() { if (++loaded >= total) setTimeout(function(){ window.print(); }, 400); }
    if (total === 0) setTimeout(function(){ window.print(); }, 400);
    else for (var i = 0; i < total; i++) { if (imgs[i].complete) tryPrint(); else { imgs[i].onload = tryPrint; imgs[i].onerror = tryPrint; } }
  </script>
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleSendEmail = async () => {
    setIsSending(true);
    try {
      await api.post("/send-architectural-request-email", {
        requestId: id,
        recipientEmail,
        ccEmail,
      });

      setSnackbar({
        open: true,
        message: "✅ Email sent successfully.",
        severity: "success",
      });
      setEmailDialogOpen(false);
    } catch (err) {
      console.error("Failed to send email:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to send email.",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ mb: 2.5 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={secondaryButtonSx}
          >
            Back
          </Button>
        </Box>

        <Box sx={{ mb: 3.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Architectural Request Details
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Review request details, letters, visits, comments, and scheduling activity.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...pageCardSx, p: 2 }}>
              <MemberCard member={member} id={id} />
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                ...pageCardSx,
                overflow: "hidden",
                borderTop: `4px solid ${priorityColor}`,
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  pt: 2.25,
                  pb: 1.5,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                      <ArchitectureIcon sx={{ color: "#2f63ad" }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Request #{requestDetails?.id}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <StatusChip requestedAt={requestDetails?.requested_at} />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      flexWrap: "wrap",
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<EmailIcon />}
                      onClick={() => setEmailDialogOpen(true)}
                      sx={primaryButtonSx}
                    >
                      Send via Email
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                      sx={secondaryButtonSx}
                    >
                      Print
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                {tabs.length > 0 ? (
                  <>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        mb: 3,
                        minHeight: "auto",
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "10px",
                          minHeight: 40,
                          color: "#5f6b7a",
                          mr: 1,
                          px: { xs: 1.5, md: 2 },
                          py: 1,
                          whiteSpace: "nowrap",
                          minWidth: "fit-content",
                        },
                        "& .Mui-selected": {
                          color: "#2f63ad !important",
                          backgroundColor: "rgba(47,99,173,0.08)",
                        },
                        "& .MuiTabs-indicator": {
                          display: "none",
                        },
                      }}
                    >
                      {tabs.map((t) => (
                        <Tab key={t.key} label={t.label} />
                      ))}
                    </Tabs>

                    <Divider sx={{ mb: 3, borderColor: "rgba(47,99,173,0.08)" }} />

                    {activeKey === "details" && (
                      <RequestDetailsPanel
                        ref={detailsPanelRef}
                        requestDetails={requestDetails}
                        member={member}
                        setContractor={setContractor}
                        setColors={setColors}
                        setLetterLogs={setLetterLogs}
                        currentUserId={currentUserId}
                        onUpdate={(updatedData) =>
                          setRequestDetails((prev) => ({ ...prev, ...updatedData }))
                        }
                      />
                    )}

                    {activeKey === "letters" && (
                      <ArchitecturalRequestLetterView
                        type={requestDetails?.status || "Approved"}
                        requestDetails={requestDetails}
                        member={member}
                      />
                    )}

                    {activeKey === "calendar" && (
                      <Calendar
                        architectural_request_id={id}
                        refreshTrigger={activeTab}
                      />
                    )}

                    {activeKey === "comments" && (
                      <ArchitecturalRequestComments
                        architectural_request_id={id}
                        currentUserId={currentUserId}
                        refreshTrigger={activeTab}
                      />
                    )}

                    {activeKey === "visits" && (
                      <ArchitecturalRequestVisits
                        architectural_request_id={id}
                        currentUserId={currentUserId}
                        refreshTrigger={activeTab}
                      />
                    )}

                    {activeKey === "fees" && (
                      <FeesTab
                        requestId={id}
                        requestTypeId={requestDetails?.request_type_id}
                        isAdmin={hasPermission("manage_architectural_billing")}
                      />
                    )}
                    {activeKey === "conditions" && (
                      <ARConditionsPanel
                        requestId={id}
                        isAdmin={hasPermission("edit_architectural_request_type")}
                      />
                    )}
                    {activeKey === "history" && (
                      <ARStatusHistoryTab requestId={id} />
                    )}
                    {activeKey === "photos" && (
                      <ARCompletionPhotosTab requestId={id} requestDetails={requestDetails} />
                    )}
                    {activeKey === "inspector" && (
                      <ARInspectorClockTab requestId={id} currentUserId={currentUserId} />
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}
                  >
                    You do not have access to any architectural request tabs.
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: "1.35rem",
              pt: 3,
              pb: 1,
              color: "#1f1f1f",
            }}
          >
            Send Request via Email
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Recipient Email"
              type="email"
              fullWidth
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              sx={textFieldSx}
            />

            <TextField
              margin="dense"
              label="CC Email (optional)"
              type="email"
              fullWidth
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              sx={textFieldSx}
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 1.5,
              justifyContent: "flex-end",
              gap: 1.25,
            }}
          >
            <Button
              onClick={() => setEmailDialogOpen(false)}
              variant="outlined"
              sx={secondaryButtonSx}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSendEmail}
              disabled={isSending || !recipientEmail}
              variant="contained"
              sx={primaryButtonSx}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%", borderRadius: 3 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <ArchitecturalRequestPrintContent
              requestDetails={requestDetails}
              member={member}
              comments={comments}
              contractor={contractor}
              colors={colors}
              files={detailsPanelRef.current?.getPrintData?.().files || []}
              filesLoading={detailsPanelRef.current?.getPrintData?.().filesLoading || false}
            />
          </div>
        </div>
      </Box>
    </LocalizationProvider>
  );
};

export default ArchitecturalRequestDetails;